import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  RefreshCw, Users, Building2, MapPin, Play, Search,
  ChevronLeft, ChevronRight, Loader2, Phone, MessageCircle,
  CheckCircle2, XCircle, Clock, Zap, Upload, Send,
  UserCheck, Bot, AlertTriangle, ArrowLeftRight, Calendar,
  Pause, Radio, Activity, X, Layers, Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const statusColors = {
  active: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-100 text-slate-600",
  handoff: "bg-amber-100 text-amber-800",
  human_takeover: "bg-blue-100 text-blue-800",
};

const agentColors = {
  tanya: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", dot: "bg-violet-500" },
  jyoti: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", dot: "bg-rose-500" },
};

/* ==================== CONVERSATION DETAIL (same as AI Agent) ==================== */
function ConversationDetail({ leadId, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyMsg, setReplyMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("ai");
  const messagesEndRef = useRef(null);

  const fetchDetail = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-agent/conversations/${leadId}`, { withCredentials: true });
      setData(res.data);
      const status = res.data?.conversation?.status;
      if (status === "human_takeover") setMode("human");
      else if (status === "active") setMode("ai");
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);
  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [data]);

  // Auto-refresh every 10s
  useEffect(() => {
    const iv = setInterval(fetchDetail, 10000);
    return () => clearInterval(iv);
  }, [fetchDetail]);

  const handleTakeover = async () => {
    try {
      await axios.post(`${API}/ai-agent/conversations/${leadId}/takeover`, {}, { withCredentials: true });
      setMode("human"); await fetchDetail(); if (onRefresh) onRefresh();
    } catch (e) { console.error(e); }
  };
  const handleResumeAI = async () => {
    try {
      await axios.post(`${API}/ai-agent/conversations/${leadId}/resume-ai`, {}, { withCredentials: true });
      setMode("ai"); await fetchDetail(); if (onRefresh) onRefresh();
    } catch (e) { console.error(e); }
  };
  const handleSendHumanReply = async () => {
    if (!replyMsg.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/ai-agent/conversations/${leadId}/human-reply`, { message: replyMsg }, { withCredentials: true });
      setReplyMsg("");
      setTimeout(async () => { await fetchDetail(); if (onRefresh) onRefresh(); setSending(false); }, 1000);
    } catch (e) { console.error(e); setSending(false); }
  };
  const handleSendTest = async () => {
    if (!replyMsg.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/ai-agent/test-chat`, { lead_id: leadId, message: replyMsg }, { withCredentials: true });
      setReplyMsg("");
      setTimeout(async () => { await fetchDetail(); if (onRefresh) onRefresh(); setSending(false); }, 2000);
    } catch (e) { console.error(e); setSending(false); }
  };

  if (loading) return <div className="p-6 text-center text-slate-500">Loading...</div>;
  if (!data) return <div className="p-6 text-center text-red-500">Error loading conversation</div>;

  const { conversation, lead } = data;
  const messages = conversation?.messages || [];
  const agentName = conversation?.agent_name || "tanya";
  const colors = agentColors[agentName] || agentColors.tanya;
  const isHumanMode = mode === "human" || conversation?.status === "human_takeover";
  const canReply = conversation?.status !== "closed";

  return (
    <div className="flex flex-col h-full" data-testid="elig-conversation-detail">
      {/* Header */}
      <div className={`flex items-center justify-between p-4 border-b ${isHumanMode ? "bg-blue-50" : colors.bg}`}>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-800">{lead?.name || "Unknown"}</h3>
            <Badge className={`text-[10px] ${colors.text} ${colors.bg} border ${colors.border}`}>
              {agentName === "tanya" ? "Tanya" : "Jyoti"}
            </Badge>
            {isHumanMode && <Badge className="text-[10px] bg-blue-100 text-blue-800 border border-blue-200">Human Mode</Badge>}
            <Badge variant="outline" className="text-[10px]">Past Patient</Badge>
          </div>
          <p className="text-sm text-slate-500 flex items-center gap-2">
            <Phone className="w-3 h-3" /> {lead?.phone} {lead?.clinic_name && `| ${lead.clinic_name}`} {lead?.city && `| ${lead.city}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={statusColors[conversation?.status] || statusColors.active}>
            {conversation?.status === "human_takeover" ? "Human Takeover" : conversation?.status}
          </Badge>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded" data-testid="close-detail">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Takeover Controls */}
      {canReply && (
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 border-b">
          {!isHumanMode ? (
            <Button size="sm" variant="outline" onClick={handleTakeover} className="text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="elig-takeover-btn">
              <UserCheck className="w-3 h-3 mr-1" /> Take Over Chat
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={handleResumeAI} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="elig-resume-ai-btn">
              <Bot className="w-3 h-3 mr-1" /> Resume AI
            </Button>
          )}
          <span className="text-xs text-slate-500">
            {isHumanMode ? `You are replying as ${agentName === "tanya" ? "Tanya" : "Jyoti"}. Customer sees your messages on WhatsApp.` : "AI is handling this chat. Click 'Take Over' to reply manually."}
          </span>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" style={{ maxHeight: "400px" }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "assistant" ? "justify-start" : "justify-end"}`}>
            <div className={`max-w-[80%] p-3 rounded-lg text-sm ${
              msg.role === "assistant"
                ? msg.is_human ? "bg-blue-50 border border-blue-200" : `${colors.bg} border ${colors.border}`
                : "bg-white border border-slate-200"
            }`}>
              <p className={`text-[10px] font-medium mb-1 ${msg.role === "assistant" ? msg.is_human ? "text-blue-700" : colors.text : "text-slate-500"}`}>
                {msg.role === "assistant" ? msg.is_human ? `${agentName === "tanya" ? "Tanya" : "Jyoti"} (Human)` : `${agentName === "tanya" ? "Tanya" : "Jyoti"} (AI)` : "Customer"}
              </p>
              {msg.metadata?.image_url && (
                <div className="mb-2">
                  {msg.metadata.image_base64 ? (
                    <img src={`data:image/jpeg;base64,${msg.metadata.image_base64}`} alt="Shared" className="max-w-full rounded-md border max-h-48 object-contain" />
                  ) : (
                    <a href={msg.metadata.image_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View shared image</a>
                  )}
                </div>
              )}
              <p className="text-slate-700 whitespace-pre-wrap">{msg.content}</p>
              <p className="text-[9px] text-slate-400 mt-1">{msg.timestamp ? new Date(msg.timestamp).toLocaleString("en-IN") : ""}</p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Input */}
      {canReply && (
        <div className="p-3 border-t bg-white">
          <div className="flex gap-2">
            <input
              value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (isHumanMode ? handleSendHumanReply() : handleSendTest())}
              placeholder={isHumanMode ? "Type your reply to customer..." : "Type as customer to test AI..."}
              className={`flex-1 px-3 py-2 text-sm border rounded-full focus:outline-none focus:ring-2 ${isHumanMode ? "focus:ring-blue-400 border-blue-200" : "focus:ring-cyan-400"}`}
              disabled={sending} data-testid="elig-reply-input"
            />
            <button onClick={isHumanMode ? handleSendHumanReply : handleSendTest} disabled={sending || !replyMsg.trim()}
              className={`p-2 text-white rounded-full disabled:opacity-50 transition ${isHumanMode ? "bg-blue-600 hover:bg-blue-700" : "bg-cyan-600 hover:bg-cyan-700"}`}
              data-testid="elig-reply-send-btn">
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1 text-center">
            {isHumanMode ? "Human mode: Your message is sent directly to customer's WhatsApp." : "Test mode: Type as customer. AI responds via real WhatsApp."}
          </p>
        </div>
      )}
    </div>
  );
}

/* ==================== BOOKINGS TAB ==================== */
function BookingsTab({ source }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/ai-agent/bookings?source=${source}`, { withCredentials: true });
        setBookings(res.data.bookings || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [source]);

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div data-testid="bookings-tab">
      {bookings.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Calendar className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>No bookings yet</p>
          <p className="text-xs mt-1">AI agent will book appointments when customers confirm</p>
        </div>
      ) : (
        <div className="space-y-2">
          {bookings.map((b) => {
            const agent = agentColors[b.agent] || agentColors.tanya;
            return (
              <Card key={b.task_id} className={`cursor-pointer hover:shadow-md transition ${selectedBooking === b.task_id ? "ring-2 ring-violet-500" : ""}`}
                onClick={() => setSelectedBooking(selectedBooking === b.task_id ? null : b.task_id)}
                data-testid={`booking-${b.task_id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${agent.bg}`}>
                        <Calendar className={`w-4 h-4 ${agent.text}`} />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-800">{b.lead?.name || "Unknown"}</p>
                        <p className="text-xs text-slate-400">{b.lead?.phone} | {b.agent || "AI"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-violet-100 text-violet-700 text-[10px]">{b.appointment_type || "Clinic Visit"}</Badge>
                      {b.is_eligible && <Badge variant="outline" className="text-[10px]">Past Patient</Badge>}
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1 pl-10">
                    {b.created_at ? new Date(b.created_at).toLocaleString("en-IN") : ""}
                    {b.lead?.city && ` | ${b.lead.city}`}
                    {b.lead?.clinic && ` | ${b.lead.clinic}`}
                    {b.lead?.source && ` | ${b.lead.source}`}
                  </p>
                  {selectedBooking === b.task_id && (
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg text-xs space-y-1 border">
                      <p><span className="font-medium text-slate-600">Lead ID:</span> {b.lead_id}</p>
                      <p><span className="font-medium text-slate-600">Agent:</span> {b.agent}</p>
                      <p><span className="font-medium text-slate-600">Status:</span> {b.status}</p>
                      <p><span className="font-medium text-slate-600">Type:</span> {b.appointment_type}</p>
                      {b.details && Object.keys(b.details).length > 0 && (
                        <div>
                          <span className="font-medium text-slate-600">Details:</span>
                          <pre className="bg-white p-2 rounded text-[10px] mt-1 overflow-x-auto">{JSON.stringify(b.details, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ==================== SCHEDULER PANEL ==================== */
function SchedulerPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try { const res = await axios.get(`${API}/eligible-leads/auto-schedule/status`, { withCredentials: true }); setStatus(res.data); }
    catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchStatus(); const iv = setInterval(fetchStatus, 10000); return () => clearInterval(iv); }, [fetchStatus]);

  const handleStart = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/eligible-leads/auto-schedule`, { batch_size: 10, pause_minutes: 10, template_id: "eligible_chat_temp" }, { withCredentials: true });
      await fetchStatus();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  const handleStop = async () => {
    try { await axios.post(`${API}/eligible-leads/auto-schedule/stop`, {}, { withCredentials: true }); await fetchStatus(); }
    catch (e) { console.error(e); }
  };
  const handlePause = async () => {
    try { await axios.post(`${API}/eligible-leads/auto-schedule/pause`, {}, { withCredentials: true }); await fetchStatus(); }
    catch (e) { console.error(e); }
  };
  const handleResume = async () => {
    try { await axios.post(`${API}/eligible-leads/auto-schedule/resume`, {}, { withCredentials: true }); await fetchStatus(); }
    catch (e) { console.error(e); }
  };

  if (!status) return null;
  const progress = status.total_to_process > 0 ? Math.round(((status.processed + status.failed) / status.total_to_process) * 100) : 0;
  const isManuallyPaused = status.manually_paused === true;

  return (
    <Card data-testid="scheduler-panel" className="border-violet-200 bg-violet-50/30">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-violet-600" />
            <h3 className="text-sm font-semibold text-violet-800">Campaign Auto-Scheduler</h3>
            {status.cohort_filter && <Badge className="bg-violet-100 text-violet-700 text-[10px]">Cohort: {status.cohort_filter}</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {status.running && !isManuallyPaused && (
              <Button variant="outline" size="sm" onClick={handlePause} data-testid="pause-scheduler-btn" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                <Pause className="w-3 h-3 mr-1" /> Pause
              </Button>
            )}
            {status.running && isManuallyPaused && (
              <Button size="sm" onClick={handleResume} data-testid="resume-scheduler-btn" className="bg-amber-500 hover:bg-amber-600 text-white">
                <Play className="w-3 h-3 mr-1" /> Resume
              </Button>
            )}
            {status.running ? (
              <Button variant="destructive" size="sm" onClick={handleStop} data-testid="stop-scheduler-btn">
                <XCircle className="w-3 h-3 mr-1" /> Stop
              </Button>
            ) : (
              <Button size="sm" onClick={handleStart} disabled={loading} data-testid="start-scheduler-btn" className="bg-violet-600 hover:bg-violet-700 text-white">
                <Play className="w-3 h-3 mr-1" /> {loading ? "Starting..." : "Start Campaign"}
              </Button>
            )}
          </div>
        </div>
        {status.running && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {isManuallyPaused ? (
                <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Pause className="w-2.5 h-2.5 mr-1" /> Manually Paused</Badge>
              ) : status.paused ? (
                <Badge className="bg-slate-100 text-slate-700 text-[10px]">Auto-pause ({status.pause_minutes}m between batches)</Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><Radio className="w-2.5 h-2.5 mr-1 animate-pulse" /> Processing</Badge>
              )}
              <span className="text-xs text-slate-500">Batch {status.current_batch}/{status.total_batches}</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div className="bg-violet-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>{status.processed} sent | {status.failed} failed</span>
              <span>{progress}%</span>
            </div>
          </div>
        )}
        {status.completed && !status.running && (
          <div className="bg-emerald-50 rounded-lg p-2 text-xs text-emerald-700">Campaign completed. {status.processed} sent, {status.failed} failed.</div>
        )}
        {!status.running && !status.completed && status.processed === 0 && (
          <p className="text-xs text-slate-500">Click &quot;Start Campaign&quot; to begin. Sends 10 messages, waits 10 min, repeats. Only one journey can run at a time.</p>
        )}
      </CardContent>
    </Card>
  );
}

/* ==================== DRILL-DOWN MODAL ==================== */
function DrillDownModal({ title, conversations, onClose, onSelectConv }) {
  if (!conversations) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[70vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-slate-800">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto max-h-[55vh] p-2 space-y-1">
          {conversations.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No conversations</p>}
          {conversations.map((c) => {
            const colors = agentColors[c.agent_name] || agentColors.tanya;
            return (
              <div key={c.lead_id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-200"
                onClick={() => { onSelectConv(c.lead_id); onClose(); }} data-testid={`drill-${c.lead_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-7 h-7 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                    <span className={`text-xs font-bold ${colors.text}`}>{(c.agent_name || "T")[0].toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{c.lead_name || "Unknown"}</p>
                    <p className="text-xs text-slate-400">{c.lead_phone}</p>
                  </div>
                </div>
                <Badge className={`text-[10px] ${statusColors[c.status] || ""}`}>{c.status}</Badge>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ==================== COHORTS MANAGER MODAL ==================== */
function CohortsManagerModal({ onClose, onRefresh }) {
  const [cohorts, setCohorts] = useState([]);
  const [expandedCohort, setExpandedCohort] = useState(null);
  const [previewTouches, setPreviewTouches] = useState({});
  const [uploadingCohort, setUploadingCohort] = useState(null);
  const [startingCohort, setStartingCohort] = useState(null);
  const [pauseMinutes, setPauseMinutes] = useState(15);
  const [batchSize, setBatchSize] = useState(10);
  const inputRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/eligible-leads/cohorts`, { withCredentials: true });
      setCohorts(r.data?.cohorts || []);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadPlaybook = async (code) => {
    if (previewTouches[code]) { setExpandedCohort(expandedCohort === code ? null : code); return; }
    try {
      const r = await axios.get(`${API}/eligible-leads/cohorts/${code}/playbook`, { withCredentials: true });
      setPreviewTouches((prev) => ({ ...prev, [code]: r.data?.touches || [] }));
      setExpandedCohort(code);
    } catch (e) { console.error(e); }
  };

  const handleCohortUpload = async (code, file) => {
    if (!file) return;
    setUploadingCohort(code);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/eligible-leads/cohorts/${code}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, withCredentials: true,
      });
      alert(`Uploaded to ${code}. ${r.data?.message || "Import running in background."}`);
      setTimeout(() => { load(); onRefresh?.(); }, 4000);
    } catch (err) {
      alert("Upload failed: " + (err.response?.data?.detail || err.message));
    }
    setUploadingCohort(null);
    if (inputRefs.current[code]) inputRefs.current[code].value = "";
  };

  const startCohortJourney = async (code) => {
    if (!confirm(`Queue journey for ALL patients in "${code}"?\n\nBatch: ${batchSize} · Pause: ${pauseMinutes}m between batches.\n\nThe campaign will be created in PAUSED state — no messages will send until you click Resume on the scheduler panel.`)) return;
    setStartingCohort(code);
    try {
      const r = await axios.post(`${API}/eligible-leads/auto-schedule`, {
        cohort: code, batch_size: batchSize, pause_minutes: pauseMinutes, start_paused: true,
      }, { withCredentials: true });
      alert(`Journey QUEUED (paused) for ${code}.\nTotal pending: ${r.data?.total_pending}\nEstimated at full pace: ${r.data?.estimated_duration_hours}h\n\nClick Resume on the scheduler panel to begin sending.`);
      onRefresh?.();
      onClose?.();  // Close modal so user sees scheduler panel with Resume button
    } catch (err) {
      alert("Failed: " + (err.response?.data?.detail || err.message));
    }
    setStartingCohort(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="cohorts-manager-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-violet-50 to-fuchsia-50">
          <div>
            <h2 className="font-bold text-slate-800 text-lg">Patient Journey Cohorts</h2>
            <p className="text-xs text-slate-500">5-cohort lifecycle engine · Upload data per cohort, preview messages, launch journey</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white rounded"><X className="w-4 h-4" /></button>
        </div>

        {/* Global scheduler controls */}
        <div className="px-4 py-3 border-b bg-slate-50 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold text-slate-600">Journey pace:</span>
          <label className="flex items-center gap-1 text-xs">
            Batch size
            <Input type="number" min={1} max={50} value={batchSize} onChange={(e) => setBatchSize(parseInt(e.target.value) || 10)} className="w-16 h-7 text-xs" data-testid="cohort-batch-size" />
          </label>
          <label className="flex items-center gap-1 text-xs">
            Pause (min)
            <Input type="number" min={1} max={120} value={pauseMinutes} onChange={(e) => setPauseMinutes(parseInt(e.target.value) || 15)} className="w-16 h-7 text-xs" data-testid="cohort-pause-minutes" />
          </label>
          <span className="text-[10px] text-slate-400 ml-auto">Only one journey can run at a time (global scheduler).</span>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-3">
          {cohorts.map((c) => (
            <div key={c.cohort} className="border border-slate-200 rounded-lg" data-testid={`cohort-row-${c.cohort}`}>
              <div className="flex items-center justify-between gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-800 text-sm">{c.label}</h3>
                    <Badge className="bg-violet-100 text-violet-700 text-[10px]">{c.touch_count} touches</Badge>
                    <Badge className="bg-slate-100 text-slate-700 text-[10px]">Total: {c.counts.total}</Badge>
                    {c.counts.pending > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pending: {c.counts.pending}</Badge>}
                    {c.counts.engaged > 0 && (
                      <button
                        onClick={() => window.dispatchEvent(new CustomEvent("cohort-filter", { detail: { cohort: c.cohort, status: "engaged" } }))}
                        className="text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 hover:bg-blue-200 transition"
                        data-testid={`engaged-${c.cohort}`}
                      >
                        Engaged: {c.counts.engaged} →
                      </button>
                    )}
                    {c.counts.appointment > 0 && (
                      <button
                        onClick={() => window.dispatchEvent(new CustomEvent("cohort-filter", { detail: { cohort: c.cohort, status: "appointment" } }))}
                        className="text-[10px] font-medium bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 hover:bg-emerald-200 transition"
                        data-testid={`booked-${c.cohort}`}
                      >
                        Booked: {c.counts.appointment} →
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{c.description}</p>
                  <p className="text-[11px] text-violet-600 mt-0.5"><b>Goal:</b> {c.goal}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    ref={(el) => { inputRefs.current[c.cohort] = el; }}
                    onChange={(e) => handleCohortUpload(c.cohort, e.target.files?.[0])}
                    className="hidden"
                    data-testid={`file-input-${c.cohort}`}
                  />
                  <Button size="sm" variant="outline" onClick={() => inputRefs.current[c.cohort]?.click()} disabled={uploadingCohort === c.cohort} data-testid={`upload-${c.cohort}`}>
                    {uploadingCohort === c.cohort ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                    Upload
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => loadPlaybook(c.cohort)} data-testid={`preview-${c.cohort}`}>
                    <Eye className="w-3 h-3 mr-1" /> {expandedCohort === c.cohort ? "Hide" : "Preview"}
                  </Button>
                  <Button size="sm" onClick={() => startCohortJourney(c.cohort)} disabled={startingCohort === c.cohort || c.counts.total === 0} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid={`start-${c.cohort}`}>
                    {startingCohort === c.cohort ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                    Start Journey
                  </Button>
                </div>
              </div>

              {expandedCohort === c.cohort && (previewTouches[c.cohort] || []).length > 0 && (
                <div className="border-t bg-slate-50 p-3 space-y-2 max-h-96 overflow-y-auto" data-testid={`playbook-${c.cohort}`}>
                  {previewTouches[c.cohort].map((t) => (
                    <div key={t.id} className="bg-white border border-slate-200 rounded p-2.5 text-xs">
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="bg-violet-100 text-violet-700 text-[10px]">Day {t.day}</Badge>
                          <Badge className="bg-slate-100 text-slate-600 text-[10px]">{t.type}</Badge>
                          <span className="font-medium text-slate-700">{t.subject}</span>
                        </div>
                        <span className="text-[9px] text-slate-400">tmpl: {t.template_id}</span>
                      </div>
                      <pre className="whitespace-pre-wrap text-[11px] text-slate-600 mt-1 font-sans leading-relaxed">{t.text}</pre>
                      {t.agent_tip && <p className="text-[10px] text-amber-700 mt-1"><b>Agent tip:</b> {t.agent_tip}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="border-t p-3 bg-slate-50 text-[10px] text-slate-500">
          <b>Note:</b> Journey touches are currently delivered via the approved <code>eligible_chat_temp</code> Karix template. Message copy is stored in DB and rendered per-patient (name / device / audiologist / price). Once Meta approves cohort-specific templates, update <code>template_id</code> in <code>/app/backend/eligible_leads/playbook.py</code> and each touch will auto-route to its correct template.
        </div>
      </div>
    </div>
  );
}

/* ==================== MAIN PAGE ==================== */
export default function EligibleLeadsDashboard({ user, logout }) {
  const [activeTab, setActiveTab] = useState("conversations"); // conversations | bookings | leads
  const [stats, setStats] = useState(null);
  const [eligStats, setEligStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [drillDown, setDrillDown] = useState(null);
  const [convPage, setConvPage] = useState(1);
  const [convTotalPages, setConvTotalPages] = useState(1);
  const [convTotal, setConvTotal] = useState(0);
  const [convSearch, setConvSearch] = useState("");

  // Leads tab state
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsPages, setLeadsPages] = useState(1);
  const [leadsSearch, setLeadsSearch] = useState("");
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCohorts, setShowCohorts] = useState(false);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      let convsUrl = `${API}/ai-agent/conversations/eligible?page=${convPage}&limit=50`;
      if (filter !== "all") convsUrl += `&status=${filter}`;
      if (agentFilter !== "all") convsUrl += `&agent=${agentFilter}`;
      if (convSearch) convsUrl += `&search=${encodeURIComponent(convSearch)}`;

      const [statsRes, convsRes] = await Promise.all([
        axios.get(`${API}/ai-agent/stats/eligible`, { withCredentials: true }),
        axios.get(convsUrl, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setConversations(convsRes.data.conversations || []);
      setConvTotalPages(convsRes.data.pages || 1);
      setConvTotal(convsRes.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter, agentFilter, convPage, convSearch]);

  const fetchEligStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/eligible-leads/stats`, { withCredentials: true });
      setEligStats(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: leadsPage, limit: 50 });
      if (leadsSearch) params.set("search", leadsSearch);
      if (selectedClinic) params.set("clinic", selectedClinic);
      if (selectedStatus) params.set("status", selectedStatus);
      if (selectedCohort) params.set("cohort", selectedCohort);
      const res = await axios.get(`${API}/eligible-leads?${params}`, { withCredentials: true });
      setLeads(res.data.leads || []);
      setLeadsTotal(res.data.total || 0);
      setLeadsPages(res.data.pages || 1);
    } catch (e) { console.error(e); }
  }, [leadsPage, leadsSearch, selectedClinic, selectedStatus, selectedCohort]);

  useEffect(() => {
    fetchData(); fetchEligStats();
    const iv = setInterval(() => { fetchData(); fetchEligStats(); }, 30000);
    return () => clearInterval(iv);
  }, [fetchData, fetchEligStats]);

  useEffect(() => { if (activeTab === "leads") fetchLeads(); }, [activeTab, fetchLeads]);

  // Listen for cohort drill-down events from CohortsManagerModal
  useEffect(() => {
    const handler = (e) => {
      const { cohort, status } = e.detail || {};
      if (!cohort) return;
      setSelectedCohort(cohort);
      setSelectedStatus(status || null);
      setLeadsPage(1);
      setActiveTab("leads");
      setShowCohorts(false);
    };
    window.addEventListener("cohort-filter", handler);
    return () => window.removeEventListener("cohort-filter", handler);
  }, []);

  const handleBatchTrigger = async (batchSize) => {
    setTriggering(true);
    try {
      const body = { batch_size: batchSize };
      if (selectedClinic) body.clinic_code = selectedClinic;
      await axios.post(`${API}/eligible-leads/trigger-batch`, body, { withCredentials: true });
      setTimeout(() => { fetchData(); fetchEligStats(); fetchLeads(); }, 3000);
    } catch (e) { console.error(e); }
    setTriggering(false);
  };

  const handleSingleTrigger = async (eligibleId) => {
    try {
      await axios.post(`${API}/eligible-leads/${eligibleId}/trigger`, {}, { withCredentials: true });
      setTimeout(() => { fetchData(); fetchEligStats(); fetchLeads(); }, 2000);
    } catch (e) { console.error(e); }
  };

  const handleClose = async (leadId) => {
    try {
      await axios.post(`${API}/ai-agent/conversations/${leadId}/close`, {}, { withCredentials: true });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/eligible-leads/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      await axios.post(`${API}/eligible-leads/import`, {}, { withCredentials: true });
      setTimeout(() => { fetchEligStats(); fetchLeads(); }, 5000);
    } catch (err) { alert("Upload failed: " + (err.response?.data?.detail || err.message)); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Drill-down handler for clickable stat cards
  const handleStatClick = async (statFilter) => {
    try {
      let url = `${API}/ai-agent/conversations/eligible?limit=500`;
      if (statFilter !== "all" && statFilter !== "total") url += `&status=${statFilter}`;
      const res = await axios.get(url, { withCredentials: true });
      const title = statFilter === "total" ? "All Conversations" : statFilter === "active" ? "Active Conversations" : statFilter === "handoff" ? "Handoff Conversations" : statFilter === "human_takeover" ? "Human Takeover" : statFilter === "closed" ? "Closed Conversations" : statFilter;
      setDrillDown({ title, conversations: res.data.conversations || [] });
    } catch (e) { console.error(e); }
  };

  const tanyaStats = stats?.by_agent?.tanya || {};
  const jyotiStats = stats?.by_agent?.jyoti || {};

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="p-6 space-y-5" data-testid="eligible-leads-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Eligible Leads — AI Agent</h1>
            <p className="text-sm text-slate-500">Past patients re-engagement via Tanya & Jyoti</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="file" ref={fileInputRef} accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" />
            <Button variant="outline" size="sm" onClick={() => setShowCohorts(true)} data-testid="open-cohorts-btn" className="border-violet-300 text-violet-700 hover:bg-violet-50">
              <Layers className="w-4 h-4 mr-1" /> Cohorts
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Upload Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => { fetchData(); fetchEligStats(); }} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Cohorts modal */}
        {showCohorts && <CohortsManagerModal onClose={() => setShowCohorts(false)} onRefresh={() => { fetchData(); fetchEligStats(); fetchLeads(); }} />}

        {/* Scheduler */}
        <SchedulerPanel />

        {/* Agent Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="agent-cards">
            <div className={`p-4 rounded-lg border-2 cursor-pointer transition ${agentFilter === "tanya" ? "border-violet-500 bg-violet-50 shadow-md" : "border-violet-100 bg-white hover:border-violet-300"}`}
              onClick={() => setAgentFilter(agentFilter === "tanya" ? "all" : "tanya")} data-testid="agent-tanya-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center text-white font-bold text-lg">T</div>
                <div>
                  <h3 className="font-semibold text-violet-800">Tanya</h3>
                  <p className="text-xs text-violet-500">AI Sales Agent</p>
                </div>
                {tanyaStats.active > 0 && <Badge className="bg-emerald-100 text-emerald-700 ml-auto">{tanyaStats.active} Active</Badge>}
              </div>
              <div className="flex gap-4 text-sm">
                <span className="text-violet-700 font-medium">{tanyaStats.total || 0} Total</span>
                <span className="text-emerald-600">{tanyaStats.active || 0} Active</span>
              </div>
            </div>
            <div className={`p-4 rounded-lg border-2 cursor-pointer transition ${agentFilter === "jyoti" ? "border-rose-500 bg-rose-50 shadow-md" : "border-rose-100 bg-white hover:border-rose-300"}`}
              onClick={() => setAgentFilter(agentFilter === "jyoti" ? "all" : "jyoti")} data-testid="agent-jyoti-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-rose-500 flex items-center justify-center text-white font-bold text-lg">J</div>
                <div>
                  <h3 className="font-semibold text-rose-800">Jyoti</h3>
                  <p className="text-xs text-rose-500">AI Sales Agent</p>
                </div>
                {jyotiStats.active > 0 && <Badge className="bg-emerald-100 text-emerald-700 ml-auto">{jyotiStats.active} Active</Badge>}
              </div>
              <div className="flex gap-4 text-sm">
                <span className="text-rose-700 font-medium">{jyotiStats.total || 0} Total</span>
                <span className="text-emerald-600">{jyotiStats.active || 0} Active</span>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards (clickable for drill-down) */}
        {stats && (
          <div className="grid grid-cols-3 md:grid-cols-7 gap-3" data-testid="stats-grid">
            {[
              { key: "total", label: "Total Chats", value: stats.total, color: "text-cyan-700", clickFilter: "total" },
              { key: "active", label: "Active", value: stats.active, color: "text-emerald-600", clickFilter: "active" },
              { key: "handoffs", label: "Handoffs", value: stats.handoffs, color: "text-amber-600", clickFilter: "handoff" },
              { key: "human_takeover", label: "Human", value: stats.human_takeover, color: "text-blue-600", clickFilter: "human_takeover" },
              { key: "bookings", label: "Bookings", value: stats.bookings, color: "text-violet-600", clickFilter: null },
              { key: "pending", label: "Pending Leads", value: stats.pending_eligible, color: "text-slate-600", clickFilter: null },
              { key: "today", label: "Today", value: stats.today, color: "text-purple-600", clickFilter: null },
            ].map((s) => (
              <Card key={s.key} className={`${s.clickFilter ? "cursor-pointer hover:shadow-md hover:border-slate-300 transition" : ""}`}
                onClick={() => s.clickFilter && handleStatClick(s.clickFilter)} data-testid={`stat-${s.key}`}>
                <CardContent className="p-3 text-center">
                  <p className={`text-xl font-bold ${s.color}`}>{s.value || 0}</p>
                  <p className="text-[10px] text-slate-500">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Main Tabs: Conversations | Bookings | Lead Database */}
        <div className="flex gap-1 border-b" data-testid="main-tabs">
          {[
            { key: "conversations", label: "Conversations", icon: MessageCircle },
            { key: "bookings", label: "Bookings Done", icon: Calendar },
            { key: "leads", label: "Lead Database", icon: Users },
          ].map((t) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                activeTab === t.key ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-700"
              }`} data-testid={`tab-${t.key}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* ===== CONVERSATIONS TAB ===== */}
        {activeTab === "conversations" && (
          <>
            {/* Status filter + Search */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="filter-tabs">
              <div className="flex gap-2 flex-wrap">
                {["all", "active", "human_takeover", "handoff", "closed"].map((f) => (
                  <button key={f} onClick={() => { setFilter(f); setConvPage(1); }}
                    className={`px-3 py-1.5 text-sm rounded-full border transition ${filter === f ? "bg-violet-700 text-white border-violet-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                    data-testid={`filter-${f}`}>
                    {f === "all" ? "All" : f === "human_takeover" ? "Human Takeover" : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
                {agentFilter !== "all" && (
                  <button onClick={() => { setAgentFilter("all"); setConvPage(1); }} className="px-3 py-1.5 text-sm rounded-full border bg-slate-100 text-slate-600 hover:bg-slate-200">Clear agent filter</button>
                )}
              </div>
              <div className="relative flex-1 max-w-xs ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search phone number..." value={convSearch} onChange={(e) => { setConvSearch(e.target.value); setConvPage(1); }} className="pl-9 h-9" data-testid="elig-conversation-search" />
              </div>
            </div>

            {/* Pagination info */}
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Showing {conversations.length} of {convTotal.toLocaleString()} conversations (Page {convPage}/{convTotalPages})</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={convPage <= 1} onClick={() => setConvPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm">{convPage} / {convTotalPages}</span>
                <Button variant="outline" size="sm" disabled={convPage >= convTotalPages} onClick={() => setConvPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ minHeight: "500px" }}>
              {/* Conversation List */}
              <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="conversation-list">
                {conversations.length === 0 && !loading && (
                  <div className="text-center py-12 text-slate-400">
                    <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p>No conversations yet</p>
                    <p className="text-xs mt-1">Start the campaign to engage past patients</p>
                  </div>
                )}
                {conversations.map((conv) => {
                  const lastMsg = conv.messages?.[conv.messages.length - 1];
                  const agentName = conv.agent_name || "tanya";
                  const colors = agentColors[agentName] || agentColors.tanya;
                  return (
                    <Card key={conv.lead_id}
                      className={`cursor-pointer hover:shadow-md transition ${selectedLead === conv.lead_id ? "ring-2 ring-violet-500" : ""}`}
                      onClick={() => setSelectedLead(conv.lead_id)} data-testid={`conv-${conv.lead_id}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className={`w-8 h-8 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                              <span className={`text-sm font-bold ${colors.text}`}>{agentName[0].toUpperCase()}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-slate-800 truncate">{conv.lead_name || "Unknown"}</p>
                              <p className="text-xs text-slate-400 truncate">{conv.lead_phone} {conv.clinic_name && `| ${conv.clinic_name}`}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge className={`text-[10px] ${statusColors[conv.status] || statusColors.active}`}>
                              {conv.status === "human_takeover" ? "Human" : conv.status}
                            </Badge>
                            {conv.status === "handoff" && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                            {conv.status === "human_takeover" && <UserCheck className="w-4 h-4 text-blue-500" />}
                          </div>
                        </div>
                        {lastMsg && (
                          <p className="text-xs text-slate-500 mt-2 truncate pl-10">
                            {lastMsg.role === "assistant" ? `${agentName === "tanya" ? "Tanya" : "Jyoti"}: ` : "Customer: "}
                            {lastMsg.content?.substring(0, 80)}...
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-1 pl-10">
                          <span className={`text-[10px] font-medium ${colors.text}`}>{agentName === "tanya" ? "Tanya" : "Jyoti"}</span>
                          <span className="text-[10px] text-slate-400">
                            {conv.updated_at ? new Date(conv.updated_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Conversation Detail */}
              <Card className="overflow-hidden" style={{ minHeight: "500px" }}>
                {selectedLead ? (
                  <ConversationDetail leadId={selectedLead} onClose={() => setSelectedLead(null)} onRefresh={fetchData} />
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-400">
                    <div className="text-center">
                      <Send className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Select a conversation to view details</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </>
        )}

        {/* ===== BOOKINGS TAB ===== */}
        {activeTab === "bookings" && <BookingsTab source="eligible" />}

        {/* ===== LEAD DATABASE TAB ===== */}
        {activeTab === "leads" && (
          <>
            {/* Clinic Breakdown */}
            {eligStats?.by_clinic?.length > 0 && (
              <Card className="mb-4" data-testid="clinic-breakdown">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Clinic Breakdown</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {eligStats.by_clinic.map((c) => (
                      <button key={c.clinic_code} onClick={() => { setSelectedClinic(selectedClinic === c.clinic_code ? null : c.clinic_code); setLeadsPage(1); }}
                        className={`flex items-center justify-between p-2.5 rounded-lg border transition text-left ${selectedClinic === c.clinic_code ? "border-violet-400 bg-violet-50" : "border-slate-200 hover:border-violet-300 hover:bg-violet-50"}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{c.clinic_name}</p>
                          <div className="flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3 text-slate-400" /><span className="text-xs text-slate-500">{c.city}</span></div>
                        </div>
                        <Badge variant="secondary" className="ml-2 text-xs shrink-0">{c.count}</Badge>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Batch Trigger */}
            <Card className="mb-4 border-violet-200 bg-violet-50/50" data-testid="batch-trigger">
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-violet-800">Manual Batch Trigger</h3>
                  <p className="text-xs text-violet-600 mt-0.5">{eligStats?.by_status?.pending || 0} pending leads {selectedClinic && `in ${selectedClinic}`}</p>
                </div>
                <Button size="sm" disabled={triggering} onClick={() => handleBatchTrigger(50)} className="bg-violet-600 hover:bg-violet-700" data-testid="trigger-batch-btn">
                  {triggering ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />} Trigger Next 50
                </Button>
              </CardContent>
            </Card>

            {/* Status Filters + Search */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
              <div className="flex gap-1 flex-wrap">
                {[{ key: null, label: "All" }, { key: "pending", label: "Pending" }, { key: "ai_triggered", label: "AI Triggered" }, { key: "engaged", label: "Engaged" }, { key: "appointment", label: "Appointments" }, { key: "not_interested", label: "Not Interested" }].map((t) => (
                  <Button key={t.key || "all"} variant={selectedStatus === t.key ? "default" : "outline"} size="sm"
                    onClick={() => { setSelectedStatus(t.key); setLeadsPage(1); }} className={selectedStatus === t.key ? "bg-slate-800" : ""}>
                    {t.label}
                  </Button>
                ))}
                {selectedCohort && (
                  <Button variant="default" size="sm" onClick={() => { setSelectedCohort(null); setLeadsPage(1); }} className="bg-violet-600 hover:bg-violet-700" data-testid="clear-cohort-filter">
                    <Layers className="w-3 h-3 mr-1" /> Cohort: {selectedCohort} <X className="w-3 h-3 ml-1" />
                  </Button>
                )}
              </div>
              <div className="relative flex-1 max-w-xs ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search name or phone..." value={leadsSearch} onChange={(e) => { setLeadsSearch(e.target.value); setLeadsPage(1); }} className="pl-9 h-9" data-testid="leads-search" />
              </div>
            </div>

            {/* Leads Table */}
            <Card data-testid="leads-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Patient</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Clinic</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Agent</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => {
                      const statusCfg = { pending: "bg-slate-100 text-slate-700", ai_triggered: "bg-blue-100 text-blue-700", engaged: "bg-emerald-100 text-emerald-700", appointment: "bg-violet-100 text-violet-700", not_interested: "bg-amber-100 text-amber-700", invalid: "bg-red-100 text-red-700" };
                      return (
                        <tr key={lead.eligible_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`lead-row-${lead.eligible_id}`}>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-slate-800">{lead.name}</p>
                            <div className="flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3 text-slate-400" /><span className="text-xs text-slate-500">{lead.phone}</span></div>
                          </td>
                          <td className="px-4 py-3"><p className="text-sm text-slate-700">{lead.clinic_name}</p><p className="text-xs text-slate-400">{lead.city}</p></td>
                          <td className="px-4 py-3"><Badge className={`${statusCfg[lead.status] || "bg-slate-100 text-slate-700"} text-xs`}>{lead.status}</Badge></td>
                          <td className="px-4 py-3">{lead.ai_agent_name ? <span className="text-sm capitalize text-slate-700">{lead.ai_agent_name}</span> : <span className="text-xs text-slate-400">-</span>}</td>
                          <td className="px-4 py-3">
                            {lead.status === "pending" && (
                              <Button variant="ghost" size="sm" onClick={() => handleSingleTrigger(lead.eligible_id)} className="text-violet-600 hover:text-violet-700 hover:bg-violet-50">
                                <Play className="w-3.5 h-3.5 mr-1" /> Trigger
                              </Button>
                            )}
                            {lead.status === "ai_triggered" && (
                              <Button variant="ghost" size="sm" onClick={() => { setActiveTab("conversations"); setSelectedLead(lead.eligible_id); }} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                                <MessageCircle className="w-3.5 h-3.5 mr-1" /> View Chat
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {leads.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-400">No eligible leads found</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Pagination */}
            {leadsPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-slate-500">Showing {((leadsPage - 1) * 50) + 1}-{Math.min(leadsPage * 50, leadsTotal)} of {leadsTotal.toLocaleString()}</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={leadsPage <= 1} onClick={() => setLeadsPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-sm text-slate-600">Page {leadsPage} of {leadsPages}</span>
                  <Button variant="outline" size="sm" disabled={leadsPage >= leadsPages} onClick={() => setLeadsPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Drill-down modal */}
      {drillDown && <DrillDownModal title={drillDown.title} conversations={drillDown.conversations} onClose={() => setDrillDown(null)} onSelectConv={(id) => { setActiveTab("conversations"); setSelectedLead(id); }} />}
    </DashboardLayout>
  );
}
